package com.iregained.site.components.sections.clinicians.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.clinicians.styles.*
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATIONS: Asymmetric Reveal
@Composable
private fun Modifier.slideUp(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 30.px).transition(
        Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
private fun Modifier.blurReveal(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0)
        .styleModifier { property("filter", if (isVisible) "blur(0px)" else "blur(10px)") }.transition(
            Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("filter", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun CliniciansWorkflowSection(data: CliniciansWorkflowSectionData = defaultCliniciansWorkflowData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "clinicians-workflow-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.steps.isNotEmpty()) {
        Column(
            modifier = WorkflowContainerStyle.toModifier().id(targetId).fillMaxWidth().backgroundColor(Colors.White)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.sectionTitle.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionTitle,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 16.px).slideUp(isVisible, 0),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    if (data.sectionSubtitle.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().maxWidth(1200.px).fontSize(18.px)
                                .fontWeight(FontWeight.Medium).color(palette.custom.darkText)
                                .textAlign(TextAlign.Center).margin(bottom = 40.px).slideUp(isVisible, 150).toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.sectionSubtitle; onDispose {}
                                    }
                                },
                        )
                    }

                    Column(modifier = WorkflowGridContainerStyle.toModifier()) {
                        data.steps.forEachIndexed { index, step ->
                            key(step.title) {
                                WorkflowStepRow(step, isDesktop, isVisible, 300 + (index * 200))
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun WorkflowStepRow(step: WorkflowStepData, isDesktop: Boolean, isVisible: Boolean, baseDelay: Int) {
    SimpleGrid(numColumns(1, lg = 2), modifier = WorkflowRowGridStyle.toModifier()) {
        if (isDesktop && !step.isImageLeft) {
            WorkflowContent(step, Modifier.slideUp(isVisible, baseDelay))
            WorkflowImage(step.imagePath, step.title, Modifier.blurReveal(isVisible, baseDelay + 150))
        } else {
            WorkflowImage(step.imagePath, step.title, Modifier.blurReveal(isVisible, baseDelay))
            WorkflowContent(step, Modifier.slideUp(isVisible, baseDelay + 150))
        }
    }
}

@Composable
private fun WorkflowImage(imagePath: String, altText: String, modifier: Modifier) {
    if (imagePath.isNotBlank()) {
        Box(modifier = WorkflowImageBoxStyle.toModifier().then(modifier)) {
            Image(src = imagePath, alt = altText, modifier = WorkflowImageStyle.toModifier())
        }
    }
}

@Composable
private fun WorkflowContent(step: WorkflowStepData, modifier: Modifier) {
    Column(
        modifier = WorkflowContentColumnStyle.toModifier().then(modifier), verticalArrangement = Arrangement.Center
    ) {
        if (step.overline.isNotBlank()) SpanText(
            text = step.overline, modifier = WorkflowOverlineStyle.toModifier().fontSize(18.px)
        )
        if (step.title.isNotBlank()) SpanText(text = step.title, modifier = WorkflowStepTitleStyle.toModifier())
        if (step.description.isNotBlank()) Div(
            attrs = WorkflowStepDescStyle.toModifier()
                .toAttrs { ref { htmlElement -> htmlElement.innerHTML = step.description; onDispose {} } })
    }
}

// ==========================================
// CMS DATA MODEL & IMAGE SPECS
// ==========================================
/**
 * CMS IMAGE UPLOAD SPECS FOR WORKFLOW STEPS:
 * @field imagePath:
 * - Aspect Ratio: 4:3 (Standard Photo) or 16:9.
 * - Recommended Dimensions: 800x600px minimum.
 * - Format: WebP (Preferred) or high-quality JPEG.
 * - Content: Real-world, high-fidelity photos of the device being used or the software dashboard in action. No abstract illustrations.
 */
data class WorkflowStepData(
    val overline: String,
    val title: String,
    val description: String,
    val imagePath: String,
    val isImageLeft: Boolean // Determines the Z-pattern layout
)

data class CliniciansWorkflowSectionData(
    val sectionId: String? = null,
    val sectionTitle: String = "Pioneering rehabilitation through personalized precision",
    val sectionSubtitle: String = "A seamless multimodal intervention for adaptability and access to a broad patient population.",
    val steps: List<WorkflowStepData> = listOf(
        WorkflowStepData(
            overline = "Step 01: Assess",
            title = "Baseline Assessment & Analytics",
            description = "Instantly capture objective baseline metrics for hand position, grip strength and range of motion. The System translates and saves physical capability into actionable clinical data and performance outcomes in real-time.",
            imagePath = Res.Images.CliniciansPage.WORKFLOW.WORKFLOW1,
            isImageLeft = true
        ), WorkflowStepData(
            overline = "Step 02: Execute",
            title = "Gamified Neuroplasticity-Driven Therapy",
            description = "Drive patient engagement through task-specific Digital Neurotherapeutic (DTx) protocols and games. Patients perform thousands of precise, voluntary controlled ADL movements masked as immersive gameplay.",
            imagePath = Res.Images.CliniciansPage.WORKFLOW.WORKFLOW2,
            isImageLeft = false
        ), WorkflowStepData(
            overline = "Step 03: Adjust",
            title = "Dynamic Force and ROM Adaptation",
            description = "Smart mechatronic sensors monitor patient fatigue in real-time, dynamically adjusting resistance & sensitivity to trigger neuroplasticity, while avoiding fatigue and keeping the patient in their optimal therapeutic zone for effective recovery.",
            imagePath = Res.Images.CliniciansPage.WORKFLOW.WORKFLOW3,
            isImageLeft = true
        )
    )
)

val defaultCliniciansWorkflowData = CliniciansWorkflowSectionData()